import { useContext, useEffect, useState } from 'react'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsTrack, useAnalyticsView } from '@/features/amplitude'
import { isNil } from 'lodash'
import { useReselector } from '@/utils/sharedHooks'

import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import { Icon, Text, Alert, ButtonVariant } from '@/ui'
import { PlaidLinkContext } from '@/features/user/settings/sections/connected-financial-institutions-card/plaid/LinkContext'
import { InstitutionList } from '@/features/user/settings/sections/connected-financial-institutions-card/InstitutionList'
import { PlaidLink } from '@/components/plaid/PlaidLink'
import { selectPlaidItems } from '@/selectors/financeSelectors'
import { fetchPlaidItemsIfNeeded } from '@/actions/plaidItemActions'
import { TypographyStyles } from '@/styles'

interface IConnectBusinessAccountsProps {
  cantFindBank: boolean
  setCantFindBank: (cantFindBank: boolean) => void
  showSkipButton: boolean
  setShowSkipButton: (showSkipButton: boolean) => void
}

export const ConnectBusinessAccounts = ({
  cantFindBank,
  setCantFindBank,
  showSkipButton,
  setShowSkipButton,
}: IConnectBusinessAccountsProps) => {
  const dispatch = useAppDispatch()
  const { plaidError } = useContext(PlaidLinkContext)
  const plaidInstitutions = useReselector(selectPlaidItems)
  const [connectButtonVariant, setConnectButtonVariant] = useState<
    ButtonVariant | undefined
  >('primary')

  const pageView = useAnalyticsView()
  const track = useAnalyticsTrack()

  useEffect(() => {
    const fetch = async () => {
      await dispatch(fetchPlaidItemsIfNeeded())
    }
    pageView('onboarding items connect financial accounts')

    fetch()
  }, [dispatch, plaidInstitutions, pageView])

  useEffect(() => {
    if (Object.keys(plaidInstitutions).length > 0) {
      setConnectButtonVariant('secondary')
    } else {
      setConnectButtonVariant('primary')
    }
  }, [plaidInstitutions])

  useEffect(() => {
    if (!isNil(plaidError)) {
      pageView('viewed plaid error')
    }
  }, [plaidError, pageView])

  const handleCantFindBank = () => {
    setCantFindBank(!cantFindBank)
    setShowSkipButton(!showSkipButton)

    if (cantFindBank) {
      track('clicked cant find bank')
    }

    if (showSkipButton) {
      track('skipped connect business account')
    }
  }

  return (
    <div className="max-w-3xl">
      <Text variant={TypographyStyles.H2}>Connect business accounts</Text>
      <Text variant={TypographyStyles.BODY_SM} className="mt-4">
        Add any of the following used for your business:
      </Text>
      <ul className="list-disc pl-5 mt-2 text-sm text-gray-700">
        <li>Business checking or savings accounts</li>
        <li>Business credit cards</li>
        <li>
          <b>Do not connect personal accounts</b> – even if they have business
          transactions
        </li>
      </ul>

      <div className="my-6">
        <InstitutionList isOnboarding />
      </div>

      <div className="flex items-center justify-between mt-6">
        <PlaidLink
          buttonVariant={connectButtonVariant}
          redirect={false}
          connectButtonText={
            <>
              <Icon icon={regular('plus')} size="sm" className="mr-2" />
              Connect Account
            </>
          }
        />
      </div>

      <div className="mt-6">
        <label className="inline-flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={cantFindBank}
            onChange={handleCantFindBank}
            className="form-checkbox"
          />
          <span className="text-sm">
            I can&apos;t find my bank or institution in Plaid
          </span>
        </label>
      </div>

      {cantFindBank && (
        <Alert variant="default" className="mt-4 space-y-2">
          <Text variant={TypographyStyles.BODY_SM}>
            <b>
              Plaid may not be compatible with some smaller banks and credit
              unions.
            </b>
          </Text>
          <Text variant={TypographyStyles.BODY_SM}>
            If you have any other business accounts, please connect them now.
            We’ll follow up with further instructions later.
          </Text>
        </Alert>
      )}

      {plaidError && (
        <Alert
          variant="error"
          className="mt-4"
          description="Your account(s) could not be connected due to a Plaid error. Please try again in a few minutes. If you keep experiencing issues, feel free to skip this step for now."
        />
      )}
    </div>
  )
}
